import React, { useState, useEffect } from "react";
import moment from "moment";
import * as Yup from "yup";
import { Box, Typography } from "@material-ui/core";
import { FieldArray, useFormikContext } from "formik";
import { useSelector } from "react-redux";
import { useSelectedTheme, useTranslate } from "../../../../../../../../Hooks";
import { handoverSectionTableHeaderData } from "./handoverSectionTableHeaderData";
import { postHandoverFrequencyTypes } from "../../../../../../PaymentPlans/constants";
import { frequencyTypes } from "../../../../../../PaymentPlans/constants";
import { GetLookupItemsByLookupTypeName } from "../../../../../../../../Services";
import { PropertyPaymentPlan } from "../../../../../../../../Enums";
import { config } from "../../../../../../../../config";
import CustomlookupSelect from "../../../../../../../../Components/V2/CustomLookupSelect/CustomlookupSelect";
import {
  BasicTable,
  CustomDatePickerComponent,
  CustomSwitch,
  CustomTableHeader,
  ReskinningInputs,
  TableEditDeleteActions,
  CustomButton,
} from "../../../../../../../../Components";

// icons
import { Calendar, PlusIcon } from "../../../../../../../../assets/icons";

// styles
import useStyles from "./styles";

const initialInstallment = (frequencyId) => ({
  installmentTypeId: config.InstallmentTypes.PostHandoverInstallment,
  amountPercentage: "",
  frequencyId,
  frequencyName: "month",
  frequencyValue: null,
  numberOfInstallment: "",
  id: crypto.randomUUID(),
});

const singleHandoverSchema = Yup.object().shape({
  amountPercentage: Yup.number()
    .typeError("Amount Percentage is required")
    .required("Amount Percentage is Required")
    .min(1, "Min amount should be at least 1%")
    .max(99, "Max amount is 99%"),
  frequencyValue: Yup.mixed().required("This field is required"),
});

function HandoverSection() {
  const styles = useStyles();
  const {
    theme: { palette },
  } = useSelectedTheme();
  const { translate } = useTranslate("NewPropertiesView");
  const { values, setFieldValue, touched, errors } = useFormikContext();
  const { isDarkMode } = useSelector((state) => state.theme);
  const [selectedTriggerType, setSelectedTriggerType] = useState(
    postHandoverFrequencyTypes.BY_MONTH
  );
  const [frequencyOptions, setFrequencyOptions] = useState([]);
  const [newInstallment, setNewInstallment] = useState(initialInstallment());
  const [localErrors, setLocalErrors] = useState({});
  const [activeItem, setActiveItem] = useState({});

  const handleNewChange = (field, value) => {
    setNewInstallment((prev) => ({ ...prev, [field]: value }));
    setLocalErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const resetForm = () => {
    const monthFrequencyId = getFrequencyIdByName("month");
    setSelectedTriggerType(postHandoverFrequencyTypes.BY_MONTH);
    setNewInstallment(initialInstallment(monthFrequencyId));
    setLocalErrors({});
  };

  const handleAdd = async (push) => {
    try {
      await singleHandoverSchema.validate(newInstallment, {
        abortEarly: false,
      });
      push({ ...newInstallment, id: crypto.randomUUID() });
      resetForm();
    } catch (err) {
      if (err.inner) {
        const errorsObj = err.inner.reduce((acc, curr) => {
          acc[curr.path] = curr.message;
          return acc;
        }, {});
        setLocalErrors(errorsObj);
      }
    }
  };

  const handleFrequencySelect = (frequencyId) => {
    const selected = frequencyOptions.find(
      (opt) => opt.lookupItemId === frequencyId
    );
    setNewInstallment((prev) => ({
      ...prev,
      frequencyId,
      frequencyName: selected?.lookupItemName?.toLowerCase() || "",
    }));
    setLocalErrors((prev) => ({ ...prev, frequencyId: undefined }));
  };

  const getFrequencyIdByName = (name) => {
    return (
      frequencyOptions.find(
        (opt) => opt.lookupItemName?.toLowerCase() === name?.toLowerCase()
      )?.lookupItemId || ""
    );
  };

  const handleTriggerTypeChange = (type) => {
    setSelectedTriggerType(type);
    if (type === postHandoverFrequencyTypes.BY_MONTH) {
      setNewInstallment((prev) => ({
        ...prev,
        frequencyName: frequencyTypes.MONTH,
        frequencyId: getFrequencyIdByName("month"),
      }));
    } else if (type === postHandoverFrequencyTypes.BY_FIXED_DATE) {
      setNewInstallment((prev) => ({
        ...prev,
        frequencyName: frequencyTypes.DATE,
        frequencyId: getFrequencyIdByName("date"),
      }));
    } else if (type === postHandoverFrequencyTypes.BY_DURATION) {
      setNewInstallment((prev) => ({
        ...prev,
        frequencyName: frequencyTypes.MONTH,
        frequencyId: getFrequencyIdByName("month"),
      }));
    }
  };

  useEffect(() => {
    GetLookupItemsByLookupTypeName({
      lookUpName: PropertyPaymentPlan.Frequency.lookupTypeName,
      pageIndex: 1,
      pageSize: 100,
    }).then((res) => {
      setFrequencyOptions(res.result || []);
      const monthOption = res.result?.find(
        (opt) => opt.lookupItemName?.toLowerCase() === "month"
      );
      if (monthOption) {
        setNewInstallment(initialInstallment(monthOption.lookupItemId));
      }
    });
  }, []);

  return (
    <Box className={styles.highlightedSections}>
      <FieldArray name="postHandoverInstallment">
        {({ push, remove }) => (
          <>
            <Box className={styles.containerInputSection}>
              <Typography className={styles.titleFiled}>
                {translate("post-handover-installment")}
              </Typography>
              <ReskinningInputs
                startAdornment={<span className={styles.adornment}>%</span>}
                inputPlaceholder={translate("e.g., 25%")}
                style={{ maxWidth: "100%" }}
                min={0}
                max={99}
                type="number"
                idRef="activitiesSearchRef"
                value={newInstallment.amountPercentage}
                onInputChanged={(e) =>
                  handleNewChange("amountPercentage", e.target.value)
                }
                error={!!localErrors.amountPercentage}
                helperText={localErrors.amountPercentage}
              />
            </Box>
            <Box className={styles.containerInputSection}>
              <Typography className={styles.titleFiled}>
                {translate("trigger-type")}
              </Typography>
              <Box
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  maxWidth: "416px",
                }}
              >
                <CustomSwitch
                  size={"md"}
                  label={translate("by-month")}
                  checked={
                    selectedTriggerType === postHandoverFrequencyTypes.BY_MONTH
                  }
                  onChange={() =>
                    handleTriggerTypeChange(postHandoverFrequencyTypes.BY_MONTH)
                  }
                  isDisabled={false}
                />
                <CustomSwitch
                  size={"md"}
                  label={translate("by-fixed-date")}
                  checked={
                    selectedTriggerType ===
                    postHandoverFrequencyTypes.BY_FIXED_DATE
                  }
                  onChange={() =>
                    handleTriggerTypeChange(
                      postHandoverFrequencyTypes.BY_FIXED_DATE
                    )
                  }
                  isDisabled={false}
                />
                <CustomSwitch
                  size={"md"}
                  label={translate("by-duration")}
                  checked={
                    selectedTriggerType ===
                    postHandoverFrequencyTypes.BY_DURATION
                  }
                  onChange={() =>
                    handleTriggerTypeChange(
                      postHandoverFrequencyTypes.BY_DURATION
                    )
                  }
                  isDisabled={false}
                />
              </Box>
            </Box>
            {selectedTriggerType === postHandoverFrequencyTypes.BY_MONTH && (
              <>
                <Box className={styles.containerInputSection}>
                  <Typography className={styles.titleFiled}>
                    {translate("number-of-installment")}
                  </Typography>
                  <ReskinningInputs
                    inputPlaceholder={translate("Enter (e.g., 10)")}
                    style={{ maxWidth: "416px" }}
                    min={0}
                    type="number"
                    idRef="activitiesSearchRef"
                    value={newInstallment.numberOfInstallment}
                    onInputChanged={(e) => {
                      handleNewChange("numberOfInstallment", e.target.value);
                    }}
                    error={!!localErrors.frequencyValue}
                    helperText={localErrors.frequencyValue}
                  />
                </Box>
                <Box className={styles.containerInputSection}>
                  <Typography className={styles.titleFiled}>
                    {translate("frequency")}
                  </Typography>
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      maxWidth: "416px",
                    }}
                  >
                    <ReskinningInputs
                      startAdornment={
                        <span className={styles.adornment}>
                          {translate("months")}
                        </span>
                      }
                      inputPlaceholder={translate("e.g., every 1, 2 months")}
                      style={{ maxWidth: "100%" }}
                      min={0}
                      type="number"
                      idRef="activitiesSearchRef"
                      value={newInstallment.frequencyValue}
                      onInputChanged={(e) => {
                        handleNewChange("frequencyValue", e.target.value);
                      }}
                      error={!!localErrors.frequencyValue}
                      helperText={localErrors.frequencyValue}
                    />
                    <Typography
                      style={{
                        color: palette.text.tertiary,
                        fontSize: "14px",
                      }}
                    >
                      {translate(
                        "set-how-often-payments-are-made(e.g., 1 for monthly, 2 for every 2 months), and so on"
                      )}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
            {selectedTriggerType ===
              postHandoverFrequencyTypes.BY_FIXED_DATE && (
              <Box className={styles.containerInputSection}>
                <Typography className={styles.titleFiled}>
                  {translate("by-fixed-date")}
                </Typography>
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    maxWidth: "416px",
                  }}
                >
                  <CustomDatePickerComponent
                    value={moment(newInstallment.frequencyValue)}
                    placeholder={"dd/mm/yyyy"}
                    customStyle={styles.datePicker}
                    withIcon
                    minDate={moment()}
                    translate={translate}
                    onDateChanged={(date) => {
                      handleNewChange(
                        "frequencyValue",
                        new Date(date).toLocaleDateString()
                      );
                    }}
                    startAdornment={
                      <Calendar
                        width="20"
                        height="20"
                        fill={palette.foreground.quarterary}
                      />
                    }
                    autoOk={translate("confirm")}
                    error={!!localErrors.frequencyValue}
                    helperText={localErrors.frequencyValue}
                  />
                  <Typography
                    style={{
                      color: palette.text.tertiary,
                      fontSize: "14px",
                    }}
                  >
                    {translate("select-installment-date")}
                  </Typography>
                </Box>
              </Box>
            )}
            {selectedTriggerType === postHandoverFrequencyTypes.BY_DURATION && (
              <Box className={styles.containerInputSection}>
                <Typography className={styles.titleFiled}>
                  {translate("by-duration")}
                </Typography>
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    maxWidth: "416px",
                  }}
                >
                  {/******** This the one that needs editing  ********/}
                  <ReskinningInputs
                    className={styles.lookupAdornment}
                    startAdornment={
                      <CustomlookupSelect
                        lookupName={
                          PropertyPaymentPlan.Frequency.lookupTypeName
                        }
                        onSelectChanged={handleFrequencySelect}
                        excludeLookupByName={["date", "day", "completion"]}
                        style={{ width: "60%" }}
                        value={newInstallment.frequencyId}
                        renderValue={(_value, item) => (
                          <strong>{item?.lookupItemName}</strong>
                        )}
                      />
                    }
                    inputPlaceholder={translate(
                      "enter-construction-percentage-(e.g., 25%)"
                    )}
                    style={{ maxWidth: "50%" }}
                    min={0}
                    type="number"
                    idRef="activitiesSearchRef"
                    value={newInstallment.frequencyValue}
                    onInputChanged={(e) => {
                      handleNewChange("frequencyValue", e.target.value);
                    }}
                    error={!!localErrors.frequencyValue}
                    helperText={localErrors.frequencyValue}
                  />
                  <Typography
                    style={{
                      color: palette.text.tertiary,
                      fontSize: "14px",
                    }}
                  >
                    {translate(
                      "select-the-timeframe-months-or-years-to-pay-the-entered-percentage"
                    )}
                  </Typography>
                </Box>
              </Box>
            )}
            <Box className={styles.containerInputSection}>
              <Typography style={{ width: "35%" }}></Typography>
              <Box display="flex" justifyContent="flex-start" width="100%">
                <CustomButton
                  boxShadow="none"
                  variant="text"
                  color="secondary"
                  style={{ color: palette.button.tertiaryGray_fg }}
                  onClick={() => handleAdd(push)}
                >
                  <PlusIcon
                    width="20"
                    height="20"
                    fill={palette.button.tertiaryGray_fg}
                  />
                  {translate("add-another")}
                </CustomButton>
              </Box>
            </Box>
            <Box className={styles.containerInputSection}>
              <BasicTable
                rowHeight={84}
                setActiveItem={setActiveItem}
                setActiveItemOnRowClick={(item) => {
                  setActiveItem(item);
                }}
                rowsData={values.postHandoverInstallment || []}
                columns={handoverSectionTableHeaderData(isDarkMode)?.map(
                  (column) => ({
                    ...column,
                    renderHeader: (params) => (
                      <CustomTableHeader
                        params={params}
                        title={column.headerName}
                        isHiddenFilter={true}
                      />
                    ),
                  })
                )}
                tableActions={({ anchorPosition }) => {
                  if (!activeItem || Object.keys(activeItem).length === 0)
                    return null;
                  const activeIndex = values.installment.findIndex(
                    (item) => item.id === activeItem.id
                  );

                  if (activeIndex === -1) return null;
                  return (
                    <TableEditDeleteActions
                      // anchorPosition={anchorPosition}
                      // onDelete={() => {
                      //   remove(activeIndex);
                      //   setActiveItem({});
                      //   setAnchorPosition({ x: 0, y: 0 });
                      // }}
                      anchorPosition={{ y: 200 }}
                      onDelete={() => alert("Delete")}
                    />
                  );
                }}
                emptyState={{ NoRowsOverlay: () => <Box></Box> }}
              />
            </Box>
          </>
        )}
      </FieldArray>
    </Box>
  );
}

export default HandoverSection;
